import { chunk, isNotEmpty, isTrue, populate } from 'my-easy-fp';
import llc from './llc';

export const MEDIAN_KOREAN_CHAR_ROW_LENGTH = 20;
export const LETTER_CONTENT_LEFT_MARGIN = 14;
export const LETTER_CONTENT_RIGHT_MARGIN = 14;

const koreanReg = /[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/;
const log = llc(__filename);

export function getCharCountMedian(counts: number[]) {
  const [, ...tail] = counts.sort((left, right) => left - right);
  return Math.floor(tail.reduce((prev, current) => prev + current) / (counts.length - 1));
}

/**
 * 전지는 A형(630 X 960 mm)과 B형 (780 X 1080 mm)이 있다. 자르는 횟수에 따라 숫자가 붙어 종이의 이름이 결정된다.
 * 예를들어 A1를 반으로 잘라서 두개로 나누면 A2이다. A형을 4등분(절)해서 나오는 크기가 가장 많이 사용하는 A4용지 이다.
 * @param width 폭
 */
export function getPaperHeight(width: number): number {
  return Math.floor(Math.ceil(width * 1.52));
}

/**
 *
 * @param size 가로 전체 길이 (px)
 * @param colSize 가로에 몇 줄을 넣을 건지
 * @returns
 */
export function getCharWidths(size: number, colSize: number): number[] {
  if (size / colSize === 0) {
    return populate(colSize).map(() => size / colSize);
  }

  const charWidth = Math.floor(size / colSize);
  const remainWidth = size - charWidth * (colSize - 1);

  return [remainWidth, ...populate(colSize - 1).map(() => charWidth)];
}

/**
 *
 * @param sizeFrom 마진 전체 길이
 * @param widths 계산된 가로 폭 배열
 * @returns
 */
export function getCharWidthWithMargins(sizeFrom: number, widths: number[]): Array<{ width: number; margin: number }> {
  const marginCount = widths.length - 1;
  const size = sizeFrom;

  if (marginCount <= 0) {
    return [];
  }

  const marginWidth = (() => {
    const rawMarginWidth = size / marginCount;

    if (size / marginCount === 0) {
      return populate(marginCount).map(() => rawMarginWidth);
    }

    const marginWidth = Math.floor(rawMarginWidth);
    const remainMarginWidth = size - marginWidth * (marginCount - 1);
    return [remainMarginWidth, ...populate(marginCount - 1).map(() => marginWidth)];
  })();

  const clonedMarginWidth = [...marginWidth];

  const charWidthWithMargins = widths.map((width) => {
    return {
      width,
      margin: clonedMarginWidth.shift() ?? 0,
    };
  });

  return charWidthWithMargins;
}

/**
 *
 * @param size 세로 전체 길이(px)
 * @param rowSize 몇 글자 들어가는지 전달
 * @param width 이미 계산된 한 글자의 폭
 */
export function getCharHeights(size: number, rowSize: number, width: number): Array<{ height: number; scale: number }> {
  if (size / rowSize === 0) {
    return populate(rowSize).map(() => {
      const height = size / rowSize;

      return {
        height,
        scale: Math.floor((height * 100) / width),
      };
    });
  }

  const charHeight = Math.floor(size / rowSize);
  const remainHeight = size - charHeight * (rowSize - 1);

  const heights = [remainHeight, ...populate(rowSize - 1).map(() => charHeight)];

  return heights.map((height) => {
    return {
      height,
      scale: Math.floor((height * 100) / width),
    };
  });
}

export function flating(contentFrom: string[][]) {
  const content = contentFrom.map((row) => [...row]);
  const [maxSize] = content.map((row) => row.length).sort((left, right) => right - left);
  const [maxSize2] = content.map((row) => row.length).sort((left, right) => left - right);

  log('[flating]maxSize: ', maxSize, maxSize2);

  return content
    .map((row) => {
      if (maxSize > row.length) {
        return [...populate(maxSize - row.length).map(() => ''), ...row];
      } else {
        return row;
      }
    })
    .flatMap((value) => value);
}

/**
 * 평문 입력된 시를 한글만 추려내서 2차원 배열로 변환
 *
 * @param content 입력한 시 내용
 * @returns 시를 적절한 레이아웃으로 재편해서 이차원 배열로 만듬
 */
export function getLetterLayout(content: string): string[][] {
  const koreans = getKorean(content);
  const langLayout = getDoubleDimensionContent(koreans, 20);
  const newColSize = getLastLineResize(langLayout);
  const refinedKoreans = isNotEmpty(newColSize) ? getDoubleDimensionContent(koreans, newColSize) : langLayout;
  const reversed = [...refinedKoreans].reverse();

  return reversed;
}

interface IInpMarginSize {
  letters: string[][];
  size: {
    width: number;
    height: number;
  };
}

export function getMarginSize({ letters, size }: IInpMarginSize) {
  // 작은 종이
  if (letters.length < 7) {
    const width = size.width / 2;
    const margin = size.width % 2 !== 0 ? size.width / 2 - 1 : size.width / 2;

    log('폭: ', width, '마진: ', margin);

    return {
      width: size.width % 2 !== 0 ? width + 1 : width,
      margin: {
        left: margin / 2,
        right: margin / 2,
      },
    };
  }

  return {
    width: size.width - LETTER_CONTENT_LEFT_MARGIN - LETTER_CONTENT_RIGHT_MARGIN,
    margin: {
      left: LETTER_CONTENT_LEFT_MARGIN,
      right: LETTER_CONTENT_RIGHT_MARGIN,
    },
  };

  // 그외
}

interface IInpRectSize {
  letters: string[][];
  size: {
    width: number;
    height: number;
  };
  margin: {
    left: number;
    right: number;
  };
}

export function getRectSize({
  size,
  letters,
  margin,
}: IInpRectSize): {
  widths: ReturnType<typeof getCharWidthWithMargins>;
  heights: Array<ReturnType<typeof getCharHeights>>;
} {
  // 6:4로 스크린 사이즈를 분할한다, 4는 좌우 2px씩 마진 입력
  const marginSize = Math.floor(Math.round(size.width * 0.35));
  const charSize = size.width - marginSize;

  const charCountMedian = getCharCountMedian(letters.map((layout) => layout.length));
  const widths = getCharWidths(charSize, letters.length);
  const heights = widths.map((width, index) => {
    if (index === 0) {
      return getCharHeights(size.height, charCountMedian, width);
    }

    return getCharHeights(size.height, letters[index].length, width);
  });

  const widthsWithMargin = getCharWidthWithMargins(marginSize, widths);

  log('W 계산: ', marginSize, charSize, widthsWithMargin);
  log('H 계산: ', heights);

  return { widths: widthsWithMargin, heights };
}

export function isKoreanChar(content: string): boolean {
  try {
    return koreanReg.test(content);
  } catch (err) {
    log('isKoreanChar:');
    log(err.message);
    log(err.stack);

    return false;
  }
}

export function getKorean(content: string): string[] {
  try {
    const korean = content.split('').filter((char) => isTrue(koreanReg.test(char)));
    return korean;
  } catch (err) {
    log('getKorean:');
    log(err.message);
    log(err.stack);

    return [];
  }
}

/**
 * 세로 1줄에 들어갈 수 있는 최대 글자수는 30자
 */
export function getDoubleDimensionContent(koreansFrom: string | string[], rowsLength: number): string[][] {
  const koreans = Array.isArray(koreansFrom) ? koreansFrom : koreansFrom.split('');
  const chunked = chunk(koreans, rowsLength);
  return chunked;
}

export function shiftLayoutedContentLeft2Right(koreans: string[][], takeOutColumn: number, sizeFrom: number): string[][] {
  if (takeOutColumn === koreans.length - 1) {
    return koreans;
  }

  const lines = koreans.map((line) => [...line].filter((char) => char !== ''));
  const size = sizeFrom > lines[0].length ? lines[0].length : sizeFrom;

  const modified: string[][] = [];
  let sliced: string[] = [];

  for (let i = 0; i < lines.length; i += 1) {
    const tmpLine = [...lines[i]];
    const newLine = tmpLine.slice(size, tmpLine.length).concat(sliced);

    sliced = tmpLine.slice(0, size);

    if (i === lines.length - 1) {
      modified.push([...sliced, ...newLine]);
    } else {
      modified.push(newLine);
    }
  }

  return modified;
}

function percent(base: number, ratio: number): number {
  return Math.floor(Math.ceil((base / ratio) * 100));
}

export function getLastLineResize(contentFrom: string[][]): number | undefined {
  const content = contentFrom.map((row) => [...row]);

  const [maxRowSize] = content.map((row) => row.length).sort((left, right) => right - left);
  const [minRowSize] = content.map((row) => row.length).sort((left, right) => left - right);
  const colSize = content.length;

  if (percent(minRowSize, maxRowSize) < 50) {
    return undefined;
  }

  const lastColInfos = populate(colSize - 1, true).map((index) => ({
    index,
    size: minRowSize - (colSize - 1) * index,
    ratio: percent(minRowSize - (colSize - 1) * index, maxRowSize + index),
  }));

  log('퍼센트: ', colSize, maxRowSize, minRowSize, Math.floor(Math.ceil(colSize / maxRowSize)) - 1, lastColInfos);

  const sortedInfo = lastColInfos
    .sort((left, right) => right.ratio - left.ratio)
    .filter((info) => info.size > 0 && info.ratio > 20 && info.ratio < 60);

  const [first] = sortedInfo;

  return maxRowSize + first.index;
}
